import { z } from 'zod';

export const PlayerSchema = z.object({
  id: z.string().uuid(),
  username: z.string().min(1).max(50),
  email: z.string().email().optional(),
  /** Track 1: guest account (no e-mail/password yet) */
  guest: z.boolean().optional(),
  createdAt: z.string().datetime(),
});
export type Player = z.infer<typeof PlayerSchema>;

export const CivilizationSchema = z.object({
  id: z.string().uuid(),
  playerId: z.string().uuid(),
  name: z.string().min(1).max(100),
  homeSystemId: z.string().uuid(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
  /** M10.3 (older clients may omit it) */
  emblem: z.string().optional(),
  /** M10.3: founded through the founding flow less than 24 h ago → the client offers the first-steps guide */
  onboarding: z.boolean().optional(),
  createdAt: z.string().datetime(),
});
export type Civilization = z.infer<typeof CivilizationSchema>;

export const MeResponseSchema = z.object({
  player: PlayerSchema,
  civilization: CivilizationSchema.nullable(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

export const DemoLoginRequestSchema = z.object({
  username: z.string().min(1).max(50).optional(),
});
export type DemoLoginRequest = z.infer<typeof DemoLoginRequestSchema>;

export const DemoLoginResponseSchema = z.object({
  token: z.string(),
  player: PlayerSchema,
  civilization: CivilizationSchema,
});
export type DemoLoginResponse = z.infer<typeof DemoLoginResponseSchema>;

// M10.2: accounts & identity (replaces the M4 username+password registration)

export const UsernameSchema = z.string().trim()
  .min(3, 'A felhasználónév legalább 3 karakter')
  .max(20, 'A felhasználónév legfeljebb 20 karakter')
  .regex(/^[a-zA-Z0-9_]+$/, 'A felhasználónévben csak betű, szám és _ lehet');

export const EmailSchema = z.string().trim().toLowerCase()
  .max(254, 'Túl hosszú e-mail-cím')
  .email('Érvénytelen e-mail-cím');

export const NewPasswordSchema = z.string()
  .min(10, 'A jelszó legalább 10 karakter')
  .max(128, 'A jelszó legfeljebb 128 karakter')
  .regex(/\p{L}/u, 'A jelszó tartalmazzon betűt')
  .regex(/[0-9]/, 'A jelszó tartalmazzon számjegyet');

/** Registration / guest upgrade: the mandatory, not pre-ticked checkbox for the Terms and the Privacy notice (+ age). */
export const ACCEPT_LEGAL_MESSAGE = 'A folytatáshoz el kell fogadnod a Felhasználási feltételeket és az Adatkezelési tájékoztatót, és nyilatkoznod kell a koráról';
const acceptLegal = z.literal(true, { errorMap: () => ({ message: ACCEPT_LEGAL_MESSAGE }) });
/** The document versions the page showed: a stale page is refused (409 legal_outdated) instead of consenting to unseen text. */
const legalVersions = z.object({ terms: z.string().max(40), privacy: z.string().max(40) }).strict().optional();

const passwordsMatch = (d: { password: string; passwordConfirm: string }) => d.password === d.passwordConfirm;
const MISMATCH = { message: 'A két jelszó nem egyezik', path: ['passwordConfirm'] };

/** Strict: unknown fields (e.g. `role`) are rejected, so privileges can never come from registration. */
export const RegisterRequestSchema = z.object({
  username: UsernameSchema,
  email: EmailSchema,
  password: NewPasswordSchema,
  passwordConfirm: z.string().max(128),
  /** M10.6: closed Alpha invitation (required when registration is invite-only) */
  inviteCode: z.string().trim().min(16).max(200).optional(),
  acceptLegal,
  legalVersions,
}).strict().refine(passwordsMatch, MISMATCH);
export type RegisterRequest = z.infer<typeof RegisterRequestSchema>;

export const RegisterResponseSchema = z.object({
  status: z.literal('verification_required'),
  message: z.string(),
});
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

/** `identifier` = username or e-mail. */
export const LoginRequestSchema = z.object({
  identifier: z.string().trim().min(1, 'Add meg a felhasználóneved vagy e-mail-címed').max(254),
  password: z.string().min(1, 'Add meg a jelszavad').max(128),
});
export type LoginRequest = z.infer<typeof LoginRequestSchema>;

export const AuthResponseSchema = z.object({
  /** Short-lived access token (Bearer). The refresh token travels only in an HttpOnly cookie. */
  token: z.string(),
  expiresAt: z.string().datetime(),
  player: PlayerSchema,
  civilization: CivilizationSchema.nullable(),
});
export type AuthResponse = z.infer<typeof AuthResponseSchema>;

export const TokenRequestSchema = z.object({ token: z.string().min(16).max(200) });
export type TokenRequest = z.infer<typeof TokenRequestSchema>;

export const EmailRequestSchema = z.object({ email: EmailSchema });
export type EmailRequest = z.infer<typeof EmailRequestSchema>;

export const ResetPasswordRequestSchema = z.object({
  token: z.string().min(16).max(200),
  password: NewPasswordSchema,
  passwordConfirm: z.string().max(128),
}).refine(passwordsMatch, MISMATCH);
export type ResetPasswordRequest = z.infer<typeof ResetPasswordRequestSchema>;

/** Track 1: a guest keeps the civilization and adds credentials (username optional). */
export const UpgradeGuestRequestSchema = z.object({
  username: UsernameSchema.optional(),
  email: EmailSchema,
  password: NewPasswordSchema,
  passwordConfirm: z.string().max(128),
  acceptLegal,
  legalVersions,
}).strict().refine(passwordsMatch, MISMATCH);
export type UpgradeGuestRequest = z.infer<typeof UpgradeGuestRequestSchema>;

export const ChangePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1, 'Add meg a jelenlegi jelszavad').max(128),
  password: NewPasswordSchema,
  passwordConfirm: z.string().max(128),
}).refine(passwordsMatch, MISMATCH);
export type ChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>;

/** Adds an e-mail address to a pre-M10.2 account (one without a verified address). */
export const SetEmailRequestSchema = z.object({
  email: EmailSchema,
  currentPassword: z.string().min(1).max(128),
});
export type SetEmailRequest = z.infer<typeof SetEmailRequestSchema>;

export const AccountProfileSchema = z.object({
  id: z.string().uuid(),
  username: z.string(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  emailVerifiedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  passwordChangedAt: z.string().datetime().nullable(),
  /** Pre-M10.2 account without a verified e-mail address. */
  legacyAccount: z.boolean(),
  /** Track 1: guest account; `upgradePending` = e-mail and password set, waiting for the verification link. */
  guest: z.boolean(),
  upgradePending: z.boolean(),
  /** One account = at most one civilization in the shared galaxy. */
  civilization: z.object({ id: z.string().uuid(), name: z.string(), color: z.string(), emblem: z.string() }).nullable(),
  /** Consent to the Terms / Privacy notice (versions, whether the player must accept again). */
  legal: z.object({
    current: z.object({ terms: z.string(), privacy: z.string() }),
    accepted: z.object({ terms: z.string(), privacy: z.string(), at: z.string() }).nullable(),
    needsAcceptance: z.boolean(),
  }).nullable(),
});
export type AccountProfile = z.infer<typeof AccountProfileSchema>;

export const SessionInfoSchema = z.object({
  id: z.string().uuid(),
  createdAt: z.string().datetime(),
  lastUsedAt: z.string().datetime(),
  expiresAt: z.string().datetime(),
  device: z.string(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof SessionInfoSchema>;

export const SessionListResponseSchema = z.object({ sessions: z.array(SessionInfoSchema) });
export type SessionListResponse = z.infer<typeof SessionListResponseSchema>;

/** Fiók törlésekor a megerősítő szó pontosan ez. */
export const DELETE_CONFIRM_WORD = 'TÖRLÉS';

/** E-mail-cím módosítása megerősítéssel: az új cím csak a levélben kapott link után lép életbe. */
export const ChangeEmailRequestSchema = z.object({
  newEmail: EmailSchema,
  currentPassword: z.string().min(1, 'Add meg a jelenlegi jelszavad').max(128),
}).strict();
export type ChangeEmailRequest = z.infer<typeof ChangeEmailRequestSchema>;

/** Fiók törlése: jelszó (vendégnél nincs) + a megerősítő szó. */
export const DeleteAccountRequestSchema = z.object({
  currentPassword: z.string().max(128).optional(),
  confirm: z.literal(DELETE_CONFIRM_WORD, { errorMap: () => ({ message: `Írd be pontosan: ${DELETE_CONFIRM_WORD}` }) }),
}).strict();
export type DeleteAccountRequest = z.infer<typeof DeleteAccountRequestSchema>;

/** Jelszóerősség 0–4 a regisztrációs képernyő jelzőjéhez (a szerver szabálya a NewPasswordSchema). */
export function passwordStrength(pw: string): { score: 0 | 1 | 2 | 3 | 4; label: string } {
  if (!pw) return { score: 0, label: '' };
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  if (/\p{Ll}/u.test(pw) && /\p{Lu}/u.test(pw)) s++;
  if (/[0-9]/.test(pw) && /[^\p{L}0-9]/u.test(pw)) s++;
  if (/^(.)\1+$/.test(pw) || /^(password|jelszo|12345|qwert)/i.test(pw)) s = Math.min(s, 1);
  const score = Math.min(4, s) as 0 | 1 | 2 | 3 | 4;
  return { score, label: ['Nagyon gyenge', 'Gyenge', 'Közepes', 'Erős', 'Nagyon erős'][score]! };
}

/** Machine-readable error codes of the auth/account endpoints. */
export type AuthErrorCode =
  | 'invalid_credentials' | 'email_not_verified' | 'username_taken' | 'too_many_attempts'
  | 'invalid_token' | 'csrf' | 'session_expired' | 'wrong_password' | 'email_taken' | 'email_not_allowed';

// M4/2: public civ info (no economic/military data)
export const CivilizationPublicSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
});
export type CivilizationPublic = z.infer<typeof CivilizationPublicSchema>;

// M4/3: Diplomacy
export const DiplomacyStatusSchema = z.enum(['neutral', 'hostile']);
export type DiplomacyStatus = z.infer<typeof DiplomacyStatusSchema>;

export const DiplomacyEntrySchema = z.object({
  civId: z.string().uuid(),
  civName: z.string(),
  civColor: z.string(),
  status: DiplomacyStatusSchema,
});
export type DiplomacyEntry = z.infer<typeof DiplomacyEntrySchema>;

export const SetDiplomacyRequestSchema = z.object({
  status: DiplomacyStatusSchema,
});
export type SetDiplomacyRequest = z.infer<typeof SetDiplomacyRequestSchema>;
